import { LiveAnnouncer } from '@angular/cdk/a11y';
import { Location } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { select, Store, } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { PaginatorPagination } from 'app/entity/models/paginator';
import { WindowRefService } from 'app/services/window-ref.service';
import { DropdownValue } from 'common/models/custom-dropdown';
import { BrowserTabTitleService } from 'core/services/browser-tab-title.service';
import { SearchResultHelper } from 'core/utils/search-result-helper';
import { PerfectScrollbarDirective } from 'ngx-perfect-scrollbar';
import { combineLatest, Observable, Subject, Subscription, } from 'rxjs';
import {
  setElementTriggeredSearch,
  setFilterPanelFullscreen,
  stopFocusRefineResultsButton,
  stopFocusResultsNumberButton,
  stopFocusSortedByButton,
  setRapidoMode
} from 'search/actions/search.actions';
import { getIsFirstPage } from 'search/reducers/history.reducer';
import { ElementTriggeredSearch, FilterPanelState, SearchResultsMetadataState } from 'search/reducers/search.reducer';
import { ConfigurationLoader } from 'shared/configuration-loader';
import { BulkFeatureKey } from '../../../bulk-select/enums/bulk-feature-key';
import { resetScrollAndSelectedResourceId } from '../../../entity/actions/entity.actions';
import { ViewType } from '../../../entity/models/view';
import { LOCAL_STORAGE } from '../../../models/local-storage';
import { FeatureToggleService } from '../../../services/feature-toggle.service';
import { defaultSearchObjectConfig } from '../../models/search-object';

import { MAX_TOTAL_PAGES, TruncatedSearch, } from '../../models/search-results';
import {
  getAdvancedMode,
  getAppliedFacetsArray,
  getDoFocusRefineResultsButton,
  getDoFocusResultsNumberButton,
  getDoFocusSortedByButton,
  getFilterPanelState,
  getSearchObject,
  getSearchResultsMetadata,
  getSelectedError,
  getSelectedLoading,
  State,
  getRapidoMode
} from '../../reducers/root.reducer';
import { ISortingValue, SearchParamsService, } from '../../services/search-params.service';
import { DiscoverV2BffService } from '../../../elements-v2/service/discover-v2-bff.service';
import { Page } from '../../../elements-v2/models/page';
import { filter } from 'rxjs/operators';
import { getEntities } from 'app/entity/reducers/entity.reducer';
import { ShareOptions } from '../../../models/MetaData';
import { SearchBarComponent } from '../../../common/components/search-bar/search-bar.component';

@Component({
  selector: 'app-search-res',
  templateUrl: './search-res.component.html',
  styleUrls: ['./search-res.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchResComponent implements OnDestroy, OnInit {
  public queryText: string;
  public isLoading: boolean;
  public showSearchInstruction: boolean;
  public isConnectionError: boolean;
  public isEmpty: boolean;
  public alternativeSearch?: string;
  public alternativeSearchText?: string;
  public moreParams: any;
  public totalResults: number;
  public pagination: PaginatorPagination;
  public calculatedTotalResults: string;
  public totalPages: number;
  public pageSize: number;
  public pageNum: number;
  public resource: string;
  public isAC: boolean;
  public linksCounter: any;
  public sortValue: ISortingValue;
  public subscriptions = new Subscription();
  public metadata: string;
  public hasPreviousUrl: boolean;
  public displayHints: boolean;
  public appliedFacetsArrayLength: number;
  public localStorage: any;
  public resultsStart: number;
  public resultsEnd: number;
  public scrollEvent: any = new Subject();
  public truncatedSearch: TruncatedSearch;
  public filterPanelState: FilterPanelState;
  public focusSortButton$: Observable<void>;
  public focusResultsNumberButton$: Observable<void>;
  public view: ViewType = ViewType.LIST;
  public bulkFeatureKey = BulkFeatureKey.SRP;

  public entities$ = this.store.select(getEntities);

  @ViewChild(PerfectScrollbarDirective) public scrollBar: PerfectScrollbarDirective;
  @ViewChild('scrollContainer', {static: true}) public scrollContainer: ElementRef;
  @ViewChild('refineResultsButton') public refineResultsButton: ElementRef;
  @ViewChild(SearchBarComponent) searchBarComponent: SearchBarComponent;

  private readonly focusSortButtonSubject = new Subject<void>();
  private readonly focusResultsNumberButtonSubject = new Subject<void>();
  public isSortVisible = false;
  public fixedHeader: boolean;
  private featureAdvancedSearch = false;
  public allowGrid = false;
  public queryParams: object;
  public srpShowShareIcon: boolean;
  public shareData: ShareOptions;
  public isRapidoMode = false;
  public featureRapidoSearch = false;
  public isCentralRequestInterface = false;

  constructor(
    private route: ActivatedRoute,
    private store: Store<State>,
    private cdRef: ChangeDetectorRef,
    private router: Router,
    private searchParamsService: SearchParamsService,
    private windowRef: WindowRefService,
    private location: Location,
    private searchResHelper: SearchResultHelper,
    private titleService: BrowserTabTitleService,
    private liveAnnouncer: LiveAnnouncer,
    private translateService: TranslateService,
    private readonly configLoader: ConfigurationLoader,
    private discoverV2BffService: DiscoverV2BffService,
    private readonly cdr: ChangeDetectorRef,
    private readonly featureToggleService: FeatureToggleService,
  ) {
    this.focusSortButton$ = this.focusSortButtonSubject.asObservable();
    this.focusResultsNumberButton$ = this.focusResultsNumberButtonSubject.asObservable();
  }

  public ngOnInit() {
    this.localStorage = this.windowRef.nativeWindow().localStorage;
    this.fixedHeader = this.configLoader.headerConfig?.fixedHeader;
    this.featureAdvancedSearch = this.featureToggleService.getToggles()['DIS-27818_2023-07-01_advanced_search'];
    this.featureRapidoSearch = this.configLoader.isRapidoConfigured;
    this.isCentralRequestInterface = this.featureToggleService.getToggles()['licenseCentralRequest'];
    this.queryParams = this.route.snapshot.queryParams;
    this.subscriptions.add(this.route.queryParams.subscribe((params) => {
      const previousQueryParams = this.router.parseUrl(this.router.url).queryParams;
      const previousQueryParamsNotEmpty = previousQueryParams && Object.keys(previousQueryParams).length;
      if (!previousQueryParamsNotEmpty && params.pageSize !== this.localStorage.getItem(LOCAL_STORAGE.PAGE_SIZE)) {
        const pageSize = params.pageSize || this.localStorage.getItem(LOCAL_STORAGE.PAGE_SIZE);
        this.router.navigate(['/search'], {queryParams: {...previousQueryParams, pageSize}});
      }
      if (this.view === ViewType.GRID) {
        this.router.navigate(['/search'], {queryParams: {...previousQueryParams, viewType: this.view}});
      }

      const queryParams = this.router.parseUrl(this.router.url).queryParams;
      if (queryParams.rapido && this.featureRapidoSearch) {
        this.store.dispatch(setRapidoMode({rapidoMode: true}));
      }

      const {query, ...remainingQueryParams} = params;
      this.shareTheLink();
      this.linksCounter = remainingQueryParams.linksCount;
      this.isAC = !remainingQueryParams.isAC || +remainingQueryParams.isAC === 1;
      this.queryText = query || '';
      this.pageSize = remainingQueryParams.pageSize ||
        this.localStorage.getItem(LOCAL_STORAGE.PAGE_SIZE) ||
        defaultSearchObjectConfig.pagination.pageSize;
      this.moreParams = remainingQueryParams;
      this.resource = remainingQueryParams.resource || remainingQueryParams.title || '';
      this.metadata = remainingQueryParams.metadata || '';
      this.sortValue = this.searchParamsService.extractValue(params);

      this.setDisplayHintsFlag();
      this.titleService.setTabTitle(this.queryText || remainingQueryParams.tabTitle || `'${this.resource}'`);

      if (!this.fixedHeader) {
        this.windowRef.resetScroll();
      }
    }));

    this.subscriptions.add(this.store.pipe(select(getRapidoMode)).subscribe((rapidoMode) => {
      this.isRapidoMode = rapidoMode;
      this.cdr.markForCheck();
    }));

    const previousQueryParams = this.router.parseUrl(this.router.url).queryParams;
    this.subscriptions.add(this.discoverV2BffService.getPage().pipe(
      filter(Boolean)
    ).subscribe(({settings}: Page) => {
      const {search} = settings;
      const { allowGrid, viewType } = search;
      this.allowGrid = allowGrid;
      this.view = previousQueryParams.viewType ? previousQueryParams.viewType : viewType;
      this.srpShowShareIcon = settings.social_media_display?.srp || false;
      this.cdr.markForCheck();
    }));

    this.subscriptions.add(combineLatest([
      this.store.pipe(select(getSearchObject)),
      this.store.pipe(select(getSearchResultsMetadata)),
    ]).subscribe(([{pageNum}, rollupResults]) => {
      this.alternativeSearch = rollupResults && rollupResults.alternativeSearch;
      this.truncatedSearch = rollupResults && rollupResults.truncatedSearch;
      this.countTotalResults(rollupResults);
      this.pageNum = pageNum;
      const accResultsEnd = this.pageSize * (+this.pageNum + 1);
      this.resultsEnd = accResultsEnd > this.totalResults ? this.totalResults : accResultsEnd;
      this.resultsStart = this.pageNum * this.pageSize + 1;
      this.isEmpty = !this.totalResults;
      this.pagination = {pageNum, totalPages: this.totalPages};
      this.setAlternativeSearchText();
      this.setDisplayHintsFlag();
      this.cdRef.markForCheck();
    }));

    this.subscriptions.add(this.store.pipe(select(getSelectedLoading)).subscribe((loading) => {
      this.isLoading = loading;
    }));

    this.subscriptions.add(this.store.pipe(select(getSelectedError)).subscribe((err) => {
      this.isConnectionError = err && err.status === 0;
    }));

    this.subscriptions.add(this.store.pipe(select(getAppliedFacetsArray))
      .subscribe((facets) => {
        this.appliedFacetsArrayLength = facets.length;
      }));

    this.subscriptions.add(this.store.pipe(select(getIsFirstPage))
      .subscribe((isFirstPage: boolean) => {
        this.hasPreviousUrl = !isFirstPage;
      }));

    this.subscriptions.add(this.store.pipe(select(getFilterPanelState)).subscribe((filterPanelState) => {
      this.filterPanelState = filterPanelState;
    }));

    this.subscriptions.add(this.store.pipe(select(getDoFocusRefineResultsButton)).subscribe((focusRefineResultsButton) => {
      if (focusRefineResultsButton) {
        this.store.dispatch(stopFocusRefineResultsButton());
        setTimeout(() => {
          this.refineResultsButton?.nativeElement.focus();
        });
      }
    }));

    this.subscriptions.add(this.store.pipe(select(getDoFocusSortedByButton)).subscribe((doFocusSortedByButton) => {
      if (doFocusSortedByButton) {
        this.store.dispatch(stopFocusSortedByButton());
        setTimeout(() => {
          this.focusSortButtonSubject.next();
        });
      }
    }));

    this.subscriptions.add(this.store.pipe(select(getDoFocusResultsNumberButton)).subscribe((doFocusResultsNumberButton) => {
      if (doFocusResultsNumberButton) {
        this.store.dispatch(stopFocusResultsNumberButton());
        setTimeout(() => {
          this.focusResultsNumberButtonSubject.next();
        });
      }
    }));

    if (this.featureAdvancedSearch) {
      this.subscriptions.add(combineLatest([
        this.route.queryParams,
        this.store.pipe(select(getAdvancedMode)),
      ]).subscribe(([, advancedMode]) => {
        this.showSearchInstruction = advancedMode && this.router.url.indexOf('/search?query=&') > -1;
        this.cdRef.markForCheck();
      }));
    }
  }

  public setAlternativeSearchText() {
    const el = document.createElement('span');
    el.innerHTML = this.alternativeSearch;
    this.alternativeSearchText = el.innerText;
  }

  public showDidYouMean(): boolean {
    return (this.alternativeSearch != null);
  }

  public setDisplayHintsFlag() {
    this.displayHints = (this.queryText || this.resource) && this.isEmpty && !this.alternativeSearch;
  }

  public sort(value: DropdownValue<ISortingValue>): void {
    const {selected, label} = value;
    const announceMessage = this.translateService.instant('sortTypeChangesAnnouncement', {value: label});
    this.liveAnnouncer.announce(announceMessage);
    this.store.dispatch(setElementTriggeredSearch({element: ElementTriggeredSearch.SORTED_BY_BUTTON}));
    const previousQueryParams = this.router.parseUrl(this.router.url).queryParams;
    this.router.navigate(['/search'], {
      queryParams: {
        ...previousQueryParams,
        query: this.queryText,
        sorting: selected.sorting,
        sortOrder: selected.sortOrder,
      },
    });
  }

  public changeNumberOfSearchResults(value: {selected: number;}): void {
    const {selected} = value;
    const announceMessage = this.translateService.instant('pageSizeChangesAnnouncement', {value: selected});
    this.liveAnnouncer.announce(announceMessage);
    this.store.dispatch(resetScrollAndSelectedResourceId());
    this.store.dispatch(setElementTriggeredSearch({element: ElementTriggeredSearch.RESULTS_NUMBER_BUTTON}));
    this.localStorage.setItem(LOCAL_STORAGE.PAGE_SIZE, selected);
    this.pageSize = selected;
    const previousQueryParams = this.router.parseUrl(this.router.url).queryParams;
    this.router.navigate(['/search'], {queryParams: {...previousQueryParams, pageNum: 0, pageSize: selected}});
  }

  public goToPage(pageNum: number): void {
    const previousQueryParams = this.router.parseUrl(this.router.url).queryParams;
    let queryParams;
    if (this.metadata) {
      queryParams = {
        ...previousQueryParams,
        metadata: this.metadata,
        resource: this.resource,
        pageNum,
      };
    } else if (this.queryText) {
      queryParams = {
        ...previousQueryParams,
        query: this.queryText,
        pageNum,
      };
    } else {
      queryParams = {
        ...previousQueryParams,
        pageNum,
      };
    }
    this.router.navigate(['/search'], {queryParams});
  }

  public goToPreviousPage() {
    this.store.dispatch(resetScrollAndSelectedResourceId());
    this.location.back();
  }

  public ngOnDestroy() {
    this.focusSortButtonSubject.complete();
    this.focusResultsNumberButtonSubject.complete();
    this.subscriptions.unsubscribe();
  }

  public onRefineToggle() {
    this.store.dispatch(setFilterPanelFullscreen({fullscreen: !this.filterPanelState.fullscreen}));
  }

  public onSortToggle() {
    this.isSortVisible = !this.isSortVisible;
  }

  public toggleView(view: ViewType) {
    this.view = view;
    this.localStorage.setItem(LOCAL_STORAGE.VIEW_TYPE, view);
    const previousQueryParams = this.router.parseUrl(this.router.url).queryParams;
    if (view === ViewType.GRID) {
      this.queryParams = {...previousQueryParams, viewType: view };
      this.router.navigate(['/search'], {queryParams: {...previousQueryParams, viewType: view }});
      return;
    }
    this.router.navigate(['/search'], { queryParams: { ...previousQueryParams, viewType: null } });
    this.queryParams = {...previousQueryParams, viewType: ViewType.LIST };
  }

  private countTotalResults(results: SearchResultsMetadataState) {
    this.totalResults = results.totalResults;
    this.calculatedTotalResults = this.searchResHelper.calculateTotalResults(this.totalResults);
    this.totalPages = results.totalPages > MAX_TOTAL_PAGES ? MAX_TOTAL_PAGES : results.totalPages;
  }

  private shareTheLink() {
    this.entities$.subscribe((entities) => {
      if (entities) {
        const item = entities.find(item => item.coverUrl?.['large'] !== '' && item.coverUrl?.['large']);
        if (item) {
          const { coverUrl } = item;
          if (coverUrl) {
            this.shareData = {
              image: coverUrl?.large || coverUrl?.medium || coverUrl?.small || ''
            };
          }
        }
      }
    });
  }

  public onRapidoSearch(): void {
    const newRapidoMode = !this.isRapidoMode;
    this.store.dispatch(setRapidoMode({rapidoMode: newRapidoMode}));
    if (this.searchBarComponent) {
      this.searchBarComponent.onRapidoSearch(true);
    }
  }
}
